import React, { useState } from "react";
import PropTypes from "prop-types";
import { Typography, Grid } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { MdError } from "react-icons/md";
import SelectPcp from "../../../../forms/SelectPcp";
import DateField from "../../../../forms/DateField";
import * as API from "../../../api";
import { useStyles } from "../../../layout/Styles";
import * as UTILS from "../../../../../helpers/Utility";
import * as CONST from "../../../Constants";
import { PatientData } from "../../../Contexts";
import UploadFile from "../../../layout/UploadFile";
import Flash from "../../../layout/Flash";


const propTypes = {
  cpr: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  handleUpdated: PropTypes.func.isRequired,
  handleFlash: PropTypes.func.isRequired,
  dateText: PropTypes.string.isRequired,
  providerText: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
};

DateProviderPrompt.prototype = propTypes;
export default function DateProviderPrompt({
  cpr,
  dispatch,
  handleUpdated,
  handleFlash,
  dateText,
  providerText,
  type,
}) {
  const patient = React.useContext(PatientData);
  const styles = useStyles();
  const [toggleProvider, setToggleProvider] = useState(false);
  const [toggleDocumentUpload, setToggleDocumentUpload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [flashMessage, setFlashMessage] = useState(
    type == "scheduled" ?
      (cpr.primary == true ?
        CONST.PRIMARY_CPR_APPT_EDIT_FLASH_PRIMARY
        : CONST.PRIMARY_CPR_APPT_EDIT_FLASH_NONPRIMARY) : null);
  let elemsAm = [7, 8, 9, 10, 11].map((i) => (
    <React.Fragment key={i}>
      <option value={i + ":00AM"}>{i + ":00AM"}</option>
      <option value={i + ":05AM"}>{i + ":05AM"}</option>
      <option value={i + ":10AM"}>{i + ":10AM"}</option>
      <option value={i + ":15AM"}>{i + ":15AM"}</option>
      <option value={i + ":20AM"}>{i + ":20AM"}</option>
      <option value={i + ":25AM"}>{i + ":25AM"}</option>
      <option value={i + ":30AM"}>{i + ":30AM"}</option>
      <option value={i + ":35AM"}>{i + ":35AM"}</option>
      <option value={i + ":40AM"}>{i + ":40AM"}</option>
      <option value={i + ":45AM"}>{i + ":45AM"}</option>
      <option value={i + ":50AM"}>{i + ":50AM"}</option>
      <option value={i + ":55AM"}>{i + ":55AM"}</option>
    </React.Fragment>
  ));

  let elemsPm = [12, 1, 2, 3, 4, 5, 6, 7].map((i) => (
    <React.Fragment key={i + "pm"}>
      <option value={i + ":00PM"}>{i + ":00PM"}</option>
      <option value={i + ":05PM"}>{i + ":05PM"}</option>
      <option value={i + ":10PM"}>{i + ":10PM"}</option>
      <option value={i + ":15PM"}>{i + ":15PM"}</option>
      <option value={i + ":20PM"}>{i + ":20PM"}</option>
      <option value={i + ":25PM"}>{i + ":25PM"}</option>
      <option value={i + ":30PM"}>{i + ":30PM"}</option>
      <option value={i + ":35PM"}>{i + ":35PM"}</option>
      <option value={i + ":40PM"}>{i + ":40PM"}</option>
      <option value={i + ":45PM"}>{i + ":45PM"}</option>
      <option value={i + ":50PM"}>{i + ":50PM"}</option>
      <option value={i + ":55PM"}>{i + ":55PM"}</option>
    </React.Fragment>
  ));

  let btnText = toggleDocumentUpload ? "Submit" : "Continue";

  const DateProviderSchema = Yup.object().shape({
    date: Yup.date().required(
      (type == "completed" ? "Completed " : type == "scheduled" ? "Appointment" : "New ") +
      " date is required"
    ),
    dateField: Yup.string().required(),
    provider: Yup.string().nullable(),
    procedure_type: Yup.string().nullable(),
    appointment_time: Yup.string().nullable(),
  });

  const handleSubmitWithFile = async (values) => {
    const formData = new FormData();
    setLoading(true);
    formData.append("cpr_id", cpr.id);
    formData.append("notification_id", cpr.current_notification_id);
    formData.append("option_value", 5);
    formData.append("image", values.image);
    values.date && formData.append("completed_on_date", values.date);
    values.provider && formData.append("attending_provider", values.provider);
    const res = await API.postCprStatusFormData(formData);
    if (res.error) {
      handleFlash({
        error: res.error,
      });
    } else {
      res.data.cpr && dispatch(res.data.cpr);
      handleFlash({
        success: CONST.FLASH.REPORTED_COMPLETED,
      });
      handleUpdated();
    }
  };

  const handleSubmit = async (values) => {
    let req = {
      notification_id: cpr.current_notification_id,
      cpr_id: cpr.id,
      option_value: CONST.DATE_PROVIDER_TYPE_OPTION_VALUE_MAPPING[type],
    };
    if (type == "completed") {
      cpr.procedure_type == "Colon Cancer Screening" && values.procedure_type
        ? (req = {
            ...req,
            procedure_type: values.procedure_type,
            completed_on_date: values.date,
            attending_provider: values.provider,
          })
        : (req = {
            ...req,
            completed_on_date: values.date,
            attending_provider: values.provider,
          });
    }
    if (type == "extension") {
      req = {
        ...req,
        due_date: values.date,
        extension_requested: true,
      };
    }
    if (type == "scheduled") {
      cpr.procedure_type == "Colon Cancer Screening" && values.procedure_type
        ? (req = {
            ...req,
            appointment_date: values.date,
            servicing_provider: values.provider,
            procedure_type: values.procedure_type,
          })
        : (req = {
            ...req,
            appointment_date: values.date,
            servicing_provider: values.provider,
          });
      if (values.appointment_time) {
        req = { ...req, appointment_time: UTILS.ConvertTime(values.appointment_time) };
      }
    }
    const res = await API.postCprStatus(req);
    if (res.data.cpr) {
      dispatch({ ...res.data.cpr, type: type });
      handleFlash({
        success:
          type == "completed"
            ? CONST.FLASH.REPORTED_COMPLETED
            : type == "extension"
            ? CONST.FLASH.EXTENSION_DUE_DATE
            : CONST.FLASH.APPOINTMENT_SCHEDULED,
      });
      handleUpdated();
    } else {
      handleFlash({
        error: res.error,
      });
    }
  };

  const InvalidDateContainer = ({type}) => {
    return(<>
      <div className={'red-text'}>
        Please select a date { ['scheduled', 'extension'].includes(type) ? 'later' : 'before' } today.
        <br />
      </div>
    </>)
  };

  const InvalidTimeContainer = ({type}) => {
    return(<>
      <div className={'red-text'}>
        Please select an appointment time.
      </div>
    </>)
  };
  return (
    <Formik
      initialValues={{
        date: "",
        dateField: "",
        provider: "",
        procedure_type: "",
        image: "",
        appointment_time: "",
      }}
      validationSchema={DateProviderSchema}
      validateOnChange={true}
      onSubmit={async (values) => {
        setLoading(true);
        if (values.image) {
          await handleSubmitWithFile(values);
        } else {
          await handleSubmit(values);
        }
      }}
    >
      {({ values, setFieldValue, errors, touched, setTouched, isSubmitting }) => {
        if (loading == true) {
          return (
            <>
              <div className="text-center">
                <i className="fa fa-spinner fa-spin fa-load-page"></i>
                <br />
                Submitting..
              </div>
            </>
          );
        }

        let dateValid = true;
        const today = new Date();
        const yesterday = new Date(today);
        const tommorow = new Date(today);
        tommorow.setDate(yesterday.getDate() + 1);
        yesterday.setDate(yesterday.getDate() - 1);

        if (['extension','scheduled'].includes(type)) {
          dateValid = values.date > yesterday;
        } else if (type == "completed") {
          dateValid = values.date < tommorow;
        }
        let showSelectPcp = cpr.primary_code == true && patient.out_of_area != true;
        let pcpValid = showSelectPcp ? values.provider != "" : true;
        let appointmentDateTimeValid = ['extension', 'completed'].includes(type) ? true : dateValid && values.appointment_time != "";
        return (
          <Form>
            {type == "completed" ? (
              <Grid container>
                <Grid item xs={12} mb={1} mt={1}>
                  <Typography variant="h5" color="#1a247e" fontWeight="600">
                    {dateText}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <DateField
                    values={values}
                    touched={touched}
                    setFieldValue={setFieldValue}
                  />
                </Grid>

                {toggleProvider && (
                  <>
                    {cpr.procedure_type == "Colon Cancer Screening" ? (
                      <>
                        <Grid item xs={12} mt={1} mb={1}>
                          <Typography variant="h5" color="#1a247e" fontWeight="600">
                            {type == "completed"
                              ? CONST.COLON_CANCER_SCREENING.PROMPT_2
                              : CONST.COLON_CANCER_SCREENING.PROMPT_1}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} mb={1}>
                          <select
                            className="time-selector"
                            placeholder="Select test type"
                            name="procedure_type"
                            onChange={(e) => {
                              setFieldValue("procedure_type", e.target.value);
                              if (e.target.value == "Cologuard") {
                                setFieldValue("provider", "Exact Sciences");
                              } else {
                                setFieldValue("provider", "");
                              }
                            }}
                          >
                            {!values.procedure_type && (
                              <option value={null}>Select test type</option>
                            )}

                            {[
                              "Cologuard",
                              "Colonoscopy",
                              "Sigmoidoscopy",
                              "Fecal Immunochemical Test",
                              "High Sensitivity FOBT",
                            ].map((i) => (
                              <option key={i} value={i}>
                                {i}
                              </option>
                            ))}
                          </select>
                        </Grid>
                        <Grid item xs={12} sm={6} mb={1}>
                          <input
                            className={styles.field}
                            name="provider"
                            placeholder="e.g. Quest Labs"
                            disabled={values.procedure_type == "Cologuard" ? true : false}
                            value={
                              values.procedure_type == "Cologuard"
                                ? "Exact Sciences"
                                : values.provider
                            }
                            onChange={(e) => setFieldValue("provider", e.target.value)}
                          />
                        </Grid>
                        {values.procedure_type == "Cologuard" && (
                          <Typography mb={2} fontSize="21px" color="#607d8b" variant="h6">
                            {CONST.COLON_CANCER_SCREENING.COLOGUARD_NO_INPUT}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <>
                        <Grid item xs={12} mt={2} className="m-t-lg">
                          <Typography variant="h5" color="#1a247e" fontWeight="600">
                            {CONST.DATE_PROVIDER_PROMPTS.COMPLETED.providerText}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} mt={1} mb={2}>
                          {showSelectPcp &&
                            (<>
                              <SelectPcp position={'relative'} settings={patient} name="pcp" required={true} setFieldValue={setFieldValue}/>
                              </>)
                          }
                          {!showSelectPcp && (
                            <>
                              <input
                                className={styles.field}
                                name="provider"
                                placeholder="e.g. Dr. Jones"
                                onChange={(e) => setFieldValue("provider", e.target.value)}
                              />
                            </>)}
                        </Grid>
                      </>
                    )}
                  </>
                )}
                {toggleDocumentUpload && (
                  <Grid item xs={12}>
                    <UploadFile
                      setAttachment={(v) => setFieldValue("image", v)}
                      image={values.image}
                    />
                    {patient.out_of_area && !values.image && (
                      <div className="error">
                        <MdError /> {"Document or image file is required."}
                      </div>
                    )}
                  </Grid>
                )}
                <Grid item xs={12} mt={3}>
                  {!toggleDocumentUpload || !toggleProvider ? (
                    <button
                      className="prompt confirm"
                      disabled={!values.date || !dateValid || !appointmentDateTimeValid}
                      onClick={(e) => {
                        e.preventDefault();
                        !toggleProvider ? setToggleProvider(true) : setToggleDocumentUpload(true);
                      }}
                    >
                      {btnText}
                    </button>
                  ) : (
                    <button
                      disabled={
                        isSubmitting || !values.date || !dateValid || !appointmentDateTimeValid || (patient.out_of_area && !values.image)
                      }
                      className="prompt confirm"
                      type="submit"
                      onClick={(e) => {
                        if (loading == true) {
                          return;
                        }
                      }}
                    >
                      {btnText}
                    </button>
                  )}
                </Grid>
              </Grid>
            ) : (
              <Grid container>
                {flashMessage && (
                  <Flash
                    flash={{
                      success: flashMessage
                    }}
                    handleClose={(e) => {
                      setFlashMessage(null);
                    }}
                  />
                )}
                <Grid item xs={12} mb={1} mt={2}>
                  <Typography variant="h5" color="#1a247e" fontWeight="600">
                    {dateText}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <DateField values={values} touched={touched} setFieldValue={setFieldValue} />
                </Grid>

                {type !== "extension" && (
                  <Grid item xs={12} sm={4}>
                    <select
                      name="appointment_time"
                      className="time-selector"
                      onChange={(e) => setFieldValue("appointment_time", e.target.value)}
                    >
                      <option>Select Time</option>
                      {elemsAm}
                      {elemsPm}
                    </select>
                  </Grid>
                )}

                {toggleProvider && (
                  <>
                    {cpr.procedure_type == "Colon Cancer Screening" ? (
                      <>
                        <Grid item xs={12} mt={1} mb={1}>
                          <Typography variant="h5" color="#1a247e" fontWeight="600">
                            {type == "completed"
                              ? CONST.COLON_CANCER_SCREENING.PROMPT_2
                              : CONST.COLON_CANCER_SCREENING.PROMPT_1}
                          </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} mb={1}>
                          <select
                            className="time-selector"
                            placeholder="Select test type"
                            name="procedure_type"
                            onChange={(e) => {
                              setFieldValue("procedure_type", e.target.value);
                              if (e.target.value == "Cologuard") {
                                setFieldValue("provider", "Exact Sciences");
                              } else {
                                setFieldValue("provider", "");
                              }
                            }}
                          >
                            {!values.procedure_type && (
                              <option value={null}>Select test type</option>
                            )}

                            {[
                              "Cologuard",
                              "Colonoscopy",
                              "Sigmoidoscopy",
                              "Fecal Immunochemical Test",
                              "High Sensitivity FOBT",
                            ].map((i) => (
                              <option key={i} value={i}>
                                {i}
                              </option>
                            ))}
                          </select>
                        </Grid>
                        <Grid item xs={12} sm={6} mb={1}>
                          <input
                            className={styles.field}
                            name="provider"
                            placeholder="e.g. Quest Labs"
                            disabled={values.procedure_type == "Cologuard" ? true : false}
                            value={
                              values.procedure_type == "Cologuard"
                                ? "Exact Sciences"
                                : values.provider
                            }
                            onChange={(e) => setFieldValue("provider", e.target.value)}
                          />
                        </Grid>
                        {values.procedure_type == "Cologuard" && (
                          <Typography mb={2} fontSize="21px" color="#607d8b" variant="h6">
                            {CONST.COLON_CANCER_SCREENING.COLOGUARD_NO_INPUT}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <>
                        <Grid item xs={12} mt={2}>
                          <Typography variant="h5" color="#1a247e" fontWeight="600">
                            {providerText}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} mt={1}>
                        {!showSelectPcp && (
                          <>
                            <input
                                id="cpr-attending-provider-input"
                                className={styles.field}
                                onChange={(e) => setFieldValue("provider", e.target.value)}
                                name="provider"
                                placeholder="e.g. Dr. Jones"
                              />
                          </>
                        )}
                        {showSelectPcp && (
                          <>
                            <SelectPcp
                              settings={patient}
                              name="pcp"
                              required={true}
                              position={'relative'}
                              setFieldValue={setFieldValue}
                            />
                          </>
                        )}
                        </Grid>
                      </>
                    )}
                  </>
                )}

                <Grid item xs={12}>
                  {!dateValid && <InvalidDateContainer type={type} />}
                  {!appointmentDateTimeValid && (<InvalidTimeContainer />)}
                </Grid>

                <Grid item xs={12} mt={3}>
                  {type !== "extension" && !toggleProvider ? (
                    <button
                      id="submit-date-prompt"
                      disabled={!values.date || !dateValid || !appointmentDateTimeValid}
                      className="prompt confirm"
                      onClick={(e) => {

                        e.preventDefault();
                        setToggleProvider(true);
                      }}
                    >
                      Continue
                    </button>
                  ) : (
                    <button
                      disabled={(!dateValid  || !appointmentDateTimeValid || !pcpValid) || isSubmitting}
                      id="submit-date-prompt"
                      className="prompt confirm"
                      type="submit"
                    >
                      Submit
                    </button>
                  )}
                </Grid>
              </Grid>
            )}
          </Form>
        );
      }}
    </Formik>
  );
}
